/**
 * The model and thinking effort, in the M7 rail's menu (stage 2a): Auto marked
 * Recommended, then each maker as a hairline group with its mark, models not
 * open yet marked Soon, and Thinking effort pinned under them. On the desk it
 * is a glass popover over the chip; on a phone it is a sheet floating 8px in.
 *
 * Effort offers only what the Workshop API accepts (low, medium, high, xhigh,
 * max: WorkshopChatPanel clamps each model to that set). Minimal is not
 * accepted there, so it is never offered.
 */
import { useState } from "react";
import { Button, Dialog, DialogTrigger } from "react-aria-components";
import { ChevronDown } from "lucide-react";
import type { ChatModel, ReasoningEffort } from "@/types/chat.type";
import { REASONING_LABELS } from "@/app/routes/dashboard/components/agentChatUi";
import { KitModal } from "@/app/routes/dashboard/components/kit/Modal";
import { KitPopover } from "@/app/routes/dashboard/components/kit/Popover";
import { ModelMenuBody } from "../../agent/rail/ModelMenuBody";
import { ModelMark } from "../../agent/rail/RailParts";
import { shortModelName } from "../workshopThread";

type Props = {
  models: ChatModel[];
  selectedModel: ChatModel | null;
  onModelChange: (model: ChatModel) => void;
  effort: ReasoningEffort;
  onEffortChange: (effort: ReasoningEffort) => void;
  disabled: boolean;
  isPhone: boolean;
};

function MenuBody({
  models,
  selectedModel,
  onModelChange,
  effort,
  onEffortChange,
}: Omit<Props, "disabled" | "isPhone">) {
  return (
    <ModelMenuBody
      models={models}
      selectedModel={selectedModel}
      onPick={onModelChange}
      effort={effort}
      onEffortChange={onEffortChange}
      effortLabelId="ws-effort-label"
    />
  );
}

export default function WorkshopModelMenu(props: Props) {
  const { selectedModel, effort, disabled, isPhone, models } = props;
  const [open, setOpen] = useState(false);
  const efforts = selectedModel?.reasoningOptions ?? [];
  const name = selectedModel?.name ?? "Model";
  const effortLabel = REASONING_LABELS[effort] ?? effort;
  const chipText = isPhone
    ? shortModelName(name)
    : `${name}${efforts.length > 0 ? ` · ${effortLabel}` : ""}`;
  const label = `Model: ${name}${efforts.length > 0 ? `, thinking effort ${effortLabel}` : ""}`;

  const chip = (
    <Button
      className="m7r-chip ws-chip"
      aria-label={label}
      isDisabled={disabled || models.length === 0}
      onPress={isPhone ? () => setOpen(true) : undefined}
    >
      <ModelMark modelId={selectedModel?.id} />
      <span className="ws-chip-text">{chipText}</span>
      <ChevronDown aria-hidden strokeWidth={1.75} />
    </Button>
  );

  if (isPhone) {
    return (
      <>
        {chip}
        <KitModal
          isOpen={open}
          onOpenChange={setOpen}
          aria-label="Model and thinking effort"
          hideCloseButton
          modalClassName="ws-dialog ws-action-sheet ws-model-sheet ws-thread-sheet"
          overlayClassName="ws-dialog-overlay"
        >
          <span className="ws-sheet-grabber" aria-hidden="true" />
          <p className="ws-sheet-title">Model</p>
          <div className="ws-model-sheet-body">
            <MenuBody {...props} />
          </div>
        </KitModal>
      </>
    );
  }

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      {chip}
      <KitPopover placement="top start" offset={8} className="wb-glass-pop ws-model-menu">
        <Dialog aria-label="Model and thinking effort" className="ws-model-dialog">
          <MenuBody {...props} />
        </Dialog>
      </KitPopover>
    </DialogTrigger>
  );
}
